/**
 * This file contains methods which are more like utils/helpers than
 * actual selectors. Need to refactor this and need to find a better
 * home for the following.
 */
import t from 'react-translate';

import { RootState } from 'redux/schemas';
import { ActivityType, ActivityKey } from 'redux/schemas/models/activity';
import { LecturePageType } from 'redux/schemas/models/lecture-page';
import { CourseAliases } from 'redux/schemas/models/course';
import { OwnerType } from 'redux/schemas/models/course-communication';
import { VideoType } from 'redux/schemas/models/video';

import { getActivity } from './course-communications';

export const getLabelForActivity = (activity: ActivityKey, aliases?: CourseAliases): string => {
  switch (activity) {
    case ActivityKey.EXERCISES:
      return aliases
        ? aliases.assignmentAliases.AssignmentTitleAlias
        : t.COURSE_COMMUNICATIONS.ACTIVITIES.ASSIGNMENT();
    case ActivityKey.QUIZZES:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.QUIZ();
    case ActivityKey.TIMED_QUIZZES:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.TIMED_EXAM();
    case ActivityKey.SURVEYS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.SURVEY();
    case ActivityKey.LECTURE_VIDEOS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.LECTURE_VIDEO();
    case ActivityKey.CUSTOM_QUESTIONS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.PEER_EVALUATION();
    case ActivityKey.TOPICS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.DISCUSSION();
    case ActivityKey.TEAM_DISCUSSION:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.TEAM_DISCUSSION();
    case ActivityKey.EXTERNAL_TOOLS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.EXTERNAL_TOOL();
    case ActivityKey.TEAM_SET:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.TEAM_FORMATION();
    case ActivityKey.GROUP_TEAM_SET:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.GROUP_FORMATION();
    case ActivityKey.LIVE_SESSION:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.LIVE_EVENT();
    case ActivityKey.PROFILE_REQUIREMENT:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.PROFILE_COMPLETION();
    case ActivityKey.POLLS:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.POLL();
    case ActivityKey.VIDEO_PRACTICES:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.VIDEO_PRACTICE();
    case ActivityKey.VIDEO_PRACTICE_FEEDBACK:
      return t.COURSE_COMMUNICATIONS.ACTIVITIES.VIDEO_PRACTICE_FEEDBACK();
    default:
      return undefined;
  }
};

export const getKeyForActivityType = (type: OwnerType): ActivityKey => {
  switch (type) {
    case ActivityType.EXERCISE:
      return ActivityKey.EXERCISES;
    case ActivityType.QUIZ:
      return ActivityKey.QUIZZES;
    case ActivityType.TIMED_EXAM:
      return ActivityKey.TIMED_QUIZZES;
    case ActivityType.SURVEY:
      return ActivityKey.SURVEYS;
    case ActivityType.LECTURE_VIDEO:
      return ActivityKey.LECTURE_VIDEOS;
    case ActivityType.PEER_EVALUATION:
      return ActivityKey.CUSTOM_QUESTIONS;
    case ActivityType.POST:
      return ActivityKey.TOPICS;
    case ActivityType.TEAM_DISCUSSION:
      return ActivityKey.TEAM_DISCUSSION;
    case ActivityType.EXTERNAL_TOOL:
      return ActivityKey.EXTERNAL_TOOLS;
    case ActivityType.TEAM_FORMATION:
      return ActivityKey.TEAM_SET;
    case ActivityType.GROUP_FORMATION:
      return ActivityKey.GROUP_TEAM_SET;
    case ActivityType.LIVE_SESSION:
      return ActivityKey.LIVE_SESSION;
    case ActivityType.PROFILE_REQUIREMENT:
      return ActivityKey.PROFILE_REQUIREMENT;
    case ActivityType.POLL:
      return ActivityKey.POLLS;
    case ActivityType.VIDEO_PRACTICE:
      return ActivityKey.VIDEO_PRACTICES;
    case ActivityType.VIDEO_PRACTICE_FEEDBACK:
      return ActivityKey.VIDEO_PRACTICE_FEEDBACK;
    default:
      return undefined;
  }
};

export const getActivityTypeForKey = (key: ActivityKey): ActivityType => {
  switch (key) {
    case ActivityKey.EXERCISES:
      return ActivityType.EXERCISE;
    case ActivityKey.QUIZZES:
      return ActivityType.QUIZ;
    case ActivityKey.TIMED_QUIZZES:
      return ActivityType.TIMED_EXAM;
    case ActivityKey.SURVEYS:
      return ActivityType.SURVEY;
    case ActivityKey.LECTURE_VIDEOS:
      return ActivityType.LECTURE_VIDEO;
    case ActivityKey.CUSTOM_QUESTIONS:
      return ActivityType.PEER_EVALUATION;
    case ActivityKey.TOPICS:
      return ActivityType.POST;
    case ActivityKey.TEAM_DISCUSSION:
      return ActivityType.TEAM_DISCUSSION;
    case ActivityKey.EXTERNAL_TOOLS:
      return ActivityType.EXTERNAL_TOOL;
    case ActivityKey.TEAM_SET:
      return ActivityType.TEAM_FORMATION;
    case ActivityKey.GROUP_TEAM_SET:
      return ActivityType.GROUP_FORMATION;
    case ActivityKey.LIVE_SESSION:
      return ActivityType.LIVE_SESSION;
    case ActivityKey.PROFILE_REQUIREMENT:
      return ActivityType.PROFILE_REQUIREMENT;
    case ActivityKey.POLLS:
      return ActivityType.POLL;
    case ActivityKey.VIDEO_PRACTICES:
      return ActivityType.VIDEO_PRACTICE;
    case ActivityKey.VIDEO_PRACTICE_FEEDBACK:
      return ActivityType.VIDEO_PRACTICE_FEEDBACK;
    default:
      return null;
  }
};

export const getVideoType = (state: RootState, id: number) => {
  const lectureVideo = state.models.lectureVideos[id];

  return state.models.video[lectureVideo?.video]?.type;
};

/**
 * Find a prefix for the given activity type and prefix it
 * with the activity's actual title
 *
 * @returns {string} i18n Prefixed Title
 */
export const getPrefixedTitle = (state: RootState, type: OwnerType, id: number, aliases: CourseAliases) => {
  if (!type || !id) {
    return null;
  }

  const activity = getActivity(state, type, id);
  if (!activity) {
    return null;
  }

  let prefix;
  switch (type) {
    case LecturePageType.LECTURE:
      prefix = aliases.lectureAliases.LectureAlias;
      break;
    case ActivityType.EXERCISE:
      prefix = aliases.assignmentAliases.AssignmentTitleAlias;
      break;
    case ActivityType.QUIZ:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.QUIZ();
      break;
    case ActivityType.TIMED_EXAM:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.TIMED_EXAM();
      break;
    case ActivityType.SURVEY:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.SURVEY();
      break;
    case ActivityType.TEAM_FORMATION:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.TEAM_FORMATION();
      break;
    case ActivityType.GROUP_FORMATION:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.GROUP_FORMATION();
      break;
    case ActivityType.POST:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.DISCUSSION();
      break;
    case ActivityType.TEAM_DISCUSSION:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.TEAM_DISCUSSION();
      break;
    case ActivityType.PEER_EVALUATION:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.PEER_EVALUATION();
      break;
    case ActivityType.EXTERNAL_TOOL:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.EXTERNAL_TOOL();
      break;
    case ActivityType.LIVE_SESSION:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.LIVE_EVENT();
      break;
    case ActivityType.LECTURE_VIDEO:
      // For video check video or audio
      prefix = getVideoType(state, id) === VideoType.AUDIO
        ? t.COURSE_COMMUNICATIONS.ACTIVITIES.AUDIO()
        : t.COURSE_COMMUNICATIONS.ACTIVITIES.VIDEO();
      break;
    case ActivityType.PROFILE_REQUIREMENT:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.PROFILE_COMPLETION();
      break;
    case ActivityType.POLL:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.POLL();
      break;
    case ActivityType.VIDEO_PRACTICE:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.VIDEO_PRACTICE();
      break;
    case ActivityType.VIDEO_PRACTICE_FEEDBACK:
      prefix = t.COURSE_COMMUNICATIONS.ACTIVITIES.VIDEO_PRACTICE_FEEDBACK();
      break;
    default:
      prefix = null;
      break;
  }

  return prefix
    ? t.COURSE_COMMUNICATIONS.ACTIVITY_TITLE(prefix, activity.title)
    : activity.title;
};
